import { DateTime, IANAZone } from "luxon";
import { z } from "zod";
import { groupIdSchema } from "./group.js";
import { playbookIdSchema } from "./playbook.js";
import {
  TeammateRoleTypes,
  teammatePermissionSchema
} from "./teammatePermissions.js";
const companyIdSchema = z.uuid().brand();
function asCompanyId(id) {
  return companyIdSchema.parse(id);
}
const billingPlans = [
  "starter-1",
  "growth-1",
  "scale-1",
  "fountain-1",
  "performance-1"
];
function isBillingPlan(p) {
  return typeof p === "string" && billingPlans.includes(p);
}
const CompanyMode = {
  RECRUITING: "recruiting"
};
const BillingBannerType = {
  /** No banner should be displayed */
  None: null,
  /** Warning: running low on interview credits */
  Warning: "warning",
  /** Expired: all interview credits have been consumed */
  Expired: "expired",
  /** ExpiringSoon: subscription is ending soon */
  ExpiringSoon: "expiring_soon"
};
const PiiRedactionType = {
  /** Redact banking information (account numbers, routing numbers, etc.) */
  BankingInformation: "banking_information",
  /** Redact US Social Security Numbers */
  SocialSecurityNumber: "us_social_security_number",
  /** Redact driver's license numbers */
  DriversLicense: "drivers_license",
  /** Redact healthcare/insurance numbers */
  HealthcareNumber: "healthcare_number",
  /** Redact credit card numbers */
  CreditCardNumber: "credit_card_number"
};
const allPiiRedactionTypes = [
  PiiRedactionType.BankingInformation,
  PiiRedactionType.SocialSecurityNumber,
  PiiRedactionType.DriversLicense,
  PiiRedactionType.HealthcareNumber,
  PiiRedactionType.CreditCardNumber
];
const piiRedactionTypeSchema = z.enum(allPiiRedactionTypes);
const ExtAccountIdSource = {
  Fountain: "fountain"
};
const allExtAccountIdSources = [ExtAccountIdSource.Fountain];
const singlePlaybookRoutingGroupRuleSchema = z.object({
  kind: z.literal("single"),
  group_id: groupIdSchema,
  playbook_id: playbookIdSchema
});
const multiPlaybookRoutingGroupRuleSchema = z.object({
  kind: z.literal("multi"),
  group_id: groupIdSchema,
  playbook_ids: z.array(playbookIdSchema).min(2).refine((ids) => new Set(ids).size === ids.length, {
    message: "playbook_ids must be unique"
  }),
  default_playbook_id: playbookIdSchema
}).refine((rule) => rule.playbook_ids.includes(rule.default_playbook_id), {
  message: "default_playbook_id must be one of playbook_ids"
});
function tagLegacyPlaybookRoutingAsSingle(raw) {
  if (raw !== null && typeof raw === "object" && !("kind" in raw) && "playbook_id" in raw) {
    const legacyRule = raw;
    return { kind: "single", ...legacyRule };
  }
  return raw;
}
const playbookRoutingGroupRuleSchema = z.preprocess(
  tagLegacyPlaybookRoutingAsSingle,
  z.discriminatedUnion("kind", [
    singlePlaybookRoutingGroupRuleSchema,
    multiPlaybookRoutingGroupRuleSchema
  ])
);
const playbookRoutingSchema = z.object({
  rules: z.array(playbookRoutingGroupRuleSchema).refine(
    (rules) => new Set(rules.map((rule) => rule.group_id)).size === rules.length,
    { message: "Each teammate group can have at most one routing rule" }
  )
});
const ssoProvisioningSettingsSchema = z.strictObject({
  enabled: z.boolean(),
  manager_sync_enabled: z.boolean().default(false),
  group_mappings: z.record(z.string().min(1), groupIdSchema).default({})
});
function resolveAutomationReportEmailsLinkOnly(value, hasSensitiveData) {
  return value ?? hasSensitiveData;
}
function isValidIanaTimeZone(timeZone) {
  try {
    Intl.DateTimeFormat(void 0, { timeZone });
    return true;
  } catch {
    return false;
  }
}
const ianaTimeZoneSchema = z.string().refine(isValidIanaTimeZone, {
  message: "Invalid IANA time zone"
});
const DEFAULT_COMPANY_TIMEZONE = "America/New_York";
function resolveCompanyTimezone(timezone) {
  if (!timezone || !isValidIanaTimeZone(timezone)) {
    return DEFAULT_COMPANY_TIMEZONE;
  }
  return timezone;
}
function getCompanyTimezone(company) {
  return resolveCompanyTimezone(company?.settings.timezone);
}
function getCompanyIanaZone(company) {
  const zone = IANAZone.create(getCompanyTimezone(company));
  if (!zone.isValid) {
    throw new Error("Expected resolved company timezone to be a valid zone");
  }
  return zone;
}
const companyTimezoneSchema = ianaTimeZoneSchema.nullable();
const CompanyRecordingTranscriptionProvider = {
  /** Use AssemblyAI's Universal-3 Pro speech model for transcription. */
  AssemblyAiUniversal3Pro: "assemblyai-universal-3-pro",
  /** Use AssemblyAI's Universal-3.5 Pro speech model for transcription. */
  AssemblyAiUniversal3_5Pro: "assemblyai-universal-3-5-pro",
  /** Use the default transcription configuration. */
  Default: null
};
const companyRecordingTranscriptionProviderSchema = z.union([
  z.literal(CompanyRecordingTranscriptionProvider.Default),
  z.literal(CompanyRecordingTranscriptionProvider.AssemblyAiUniversal3Pro),
  z.literal(CompanyRecordingTranscriptionProvider.AssemblyAiUniversal3_5Pro)
]);
const CompanyInterviewCallProvider = {
  /** Use LiveKit voice agent for interviews in American English only (using state of the art Deepgram voice models). */
  LivekitAmericanEnglish: "livekit-py/en-US",
  /**
   * Use the Retell v2 agent for American English recruiting interviews only.
   * Non-American-English voices, non-English languages, and non-recruiting
   * projects follow the existing provider/agent selection — which still routes
   * Retell-unsupported languages (e.g. Georgian) to LiveKit via
   * checkShouldUseLivekit().
   */
  RetellV2AmericanEnglish: "retell-v2/en-US",
  /** Use default. Usually Retell, but it depends on project settings and interview language. See checkShouldUseLivekit(). */
  Default: null
};
const companyInterviewCallProviderSchema = z.union([
  z.literal(CompanyInterviewCallProvider.Default),
  z.literal(CompanyInterviewCallProvider.LivekitAmericanEnglish),
  z.literal(CompanyInterviewCallProvider.RetellV2AmericanEnglish)
]);
const ANALYZED_MINUTES_BURST_TAIL_DAYS = 7;
function resolveAnalyzedMinutesPerUser(rateLimiting, day) {
  const standard = rateLimiting?.analyzed_minutes_per_user_per_rolling_7_days;
  if (standard === null || standard === void 0) {
    return null;
  }
  const burst = rateLimiting?.analyzed_minutes_burst;
  if (burst && burst.starts_on <= day && DateTime.fromISO(day) <= DateTime.fromISO(burst.ends_on).plus({
    days: ANALYZED_MINUTES_BURST_TAIL_DAYS
  })) {
    return burst.per_user_per_rolling_7_days;
  }
  return standard;
}
const companyPatchPayloadSchema = z.strictObject({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  settings: z.strictObject({
    interview_color: z.string().nullable().optional(),
    welcome_message: z.string().nullable().optional(),
    outro_message: z.string().nullable().optional(),
    reapply_policy: z.object({
      enabled: z.boolean(),
      threshold_in_days: z.number()
    }).nullable().optional(),
    scheduling_availability: z.object({
      blocked_dates: z.array(z.string())
    }).optional(),
    recording_summary_context: z.string().nullable().optional(),
    should_send_recording_summary_email: z.boolean().optional(),
    /** @deprecated Configured on the coaching automation instead. */
    should_send_rep_coaching_emails: z.boolean().optional(),
    /** @deprecated Configured on the coaching automation instead. */
    should_send_manager_coaching_emails: z.boolean().optional(),
    domain_signup_teammate_permissions: teammatePermissionSchema.optional(),
    is_clickhouse_enabled: z.boolean().optional(),
    magic_link_auth_enabled: z.boolean().optional(),
    /** Null clears the override and falls back to {@link DEFAULT_COMPANY_TIMEZONE}. */
    timezone: companyTimezoneSchema.optional()
  }).optional()
});
const defaultReapplyPolicyThreshold = 30;
const defaultReapplyPolicy = {
  enabled: true,
  threshold_in_days: defaultReapplyPolicyThreshold
};
function getDefaultCompanySettings() {
  return {
    is_sending_invites_blocked: false,
    logo_file_name: null,
    favicon_file_name: null,
    interview_color: null,
    mode: null,
    whitelabel_domain: null,
    whitelabel_contact_email: null,
    welcome_message: null,
    outro_message: null,
    identity_provider_id: null,
    partner_identity_provider_id: null,
    is_cheating_detection_enabled: false,
    ext_account_id: null,
    domain_signup_teammate_permissions: {
      role: {
        type: TeammateRoleTypes.EDITOR
      }
    },
    reapply_policy: defaultReapplyPolicy,
    should_send_recording_summary_email: false,
    should_send_rep_coaching_emails: false,
    should_send_manager_coaching_emails: false,
    automation_report_emails_link_only: null,
    recording_summary_context: null,
    rate_limiting: null,
    voice_agent: null,
    interview_call_provider: CompanyInterviewCallProvider.RetellV2AmericanEnglish,
    recording_transcription_provider: CompanyRecordingTranscriptionProvider.Default,
    recording_transcription_key_terms: [],
    playbook_routing: {
      rules: []
    },
    billing_banner: BillingBannerType.None,
    pii_redaction: [],
    is_clickhouse_enabled: false,
    livekit_agent_flags: [],
    criteria_coverage_enabled: false,
    performance_default_date_range: null,
    timezone: null,
    magic_link_auth_enabled: false,
    ensure_recordings_browser_playable: false
  };
}
function mapCompanyDBToCompany(c) {
  const settings = {
    ...getDefaultCompanySettings(),
    ...typeof c.settings === "object" ? c.settings : {}
  };
  settings.playbook_routing = {
    ...settings.playbook_routing,
    rules: settings.playbook_routing.rules.map(
      tagLegacyPlaybookRoutingAsSingle
    )
  };
  const company = {
    id: c.id,
    name: c.name,
    description: c.description,
    billing_company_id: c.billing_company_id,
    billing_current_plan: c.billing_current_plan ? c.billing_current_plan : null,
    has_sensitive_data: Boolean(c.has_sensitive_data),
    settings,
    created_at: c.created_at,
    updated_at: c.updated_at,
    trial_expires_at: c.trial_expires_at,
    slug: c.slug
  };
  return company;
}
function mapCompanyDomainDBToCompanyDomain(c) {
  return {
    domain: c.domain,
    company_id: c.company_id
  };
}
export {
  BillingBannerType,
  CompanyInterviewCallProvider,
  CompanyMode,
  CompanyRecordingTranscriptionProvider,
  DEFAULT_COMPANY_TIMEZONE,
  ExtAccountIdSource,
  PiiRedactionType,
  allPiiRedactionTypes,
  asCompanyId,
  companyIdSchema,
  companyInterviewCallProviderSchema,
  companyPatchPayloadSchema,
  companyRecordingTranscriptionProviderSchema,
  companyTimezoneSchema,
  defaultReapplyPolicy,
  defaultReapplyPolicyThreshold,
  getCompanyIanaZone,
  getCompanyTimezone,
  getDefaultCompanySettings,
  ianaTimeZoneSchema,
  isBillingPlan,
  isValidIanaTimeZone,
  mapCompanyDBToCompany,
  mapCompanyDomainDBToCompanyDomain,
  multiPlaybookRoutingGroupRuleSchema,
  piiRedactionTypeSchema,
  playbookRoutingGroupRuleSchema,
  playbookRoutingSchema,
  resolveAnalyzedMinutesPerUser,
  resolveAutomationReportEmailsLinkOnly,
  resolveCompanyTimezone,
  singlePlaybookRoutingGroupRuleSchema,
  ssoProvisioningSettingsSchema
};
//# sourceMappingURL=company.js.map
